import React, { useMemo } from "react";

import { Chip, Text } from "@performyard/py-components";
import { SurveyQuestionsResultsResponse } from "@performyard/service-contracts";

import { ScrollableColumn } from "../../../../../../../components/TableLite/TableLiteScrollable/TableLiteScrollable";

export const getSurveyResultsColumns: () => ScrollableColumn[] = () => [
	{
		id: "question_type",
		label: "Type",
		width: 120,
		getter: (row: SurveyQuestionsResultsResponse["questions"][number]) => {
			return row.question_type;
		},
	},
	{
		id: "question_text",
		label: "Question",
		width: 678,
		getter: (row: SurveyQuestionsResultsResponse["questions"][number]) => {
			return (
				<div data-testid="question-text-cell" className="w-full">
					<Text size="paragraph-3" weight="regular" dangerouslySetClassName="py-line-clamp-1">
						{row.question_text}
					</Text>
				</div>
			);
		},
	},
	{
		id: "top_response",
		label: "Top Response",
		isSortable: false,
		width: 360,
		getter: (row: SurveyQuestionsResultsResponse["questions"][number]) => {
			return <TopResonseCell optionsResults={row.options_results} totalResponses={row.response_count} />;
		},
	},
	{
		id: "response_count",
		label: "# of Responses",
		width: 114,
		getter: (row: SurveyQuestionsResultsResponse["questions"][number]) => {
			return <div className="flex w-full justify-center">{row.response_count}</div>;
		},
	},
];

const TopResonseCell = ({
	optionsResults,
	totalResponses,
}: {
	optionsResults: SurveyQuestionsResultsResponse["questions"][number]["options_results"];
	totalResponses: number;
}) => {
	const [topResonseText, percentage]: [string | null, number | null] = useMemo(() => {
		if (!optionsResults?.length || totalResponses === 0) return [null, null];

		const sortedOptions = [...optionsResults].sort((a, b) => b.response_count - a.response_count);
		const topOption = sortedOptions[0];

		if (!topOption || topOption.response_count === 0) return [null, null];

		const isTied = sortedOptions.length > 1 && topOption.response_count === sortedOptions[1].response_count;

		if (isTied) {
			const tiedCount = sortedOptions.filter(
				(option) => option.response_count === topOption.response_count
			).length;
			return [`Tied Between ${tiedCount} Responses`, (topOption.response_count / totalResponses) * 100];
		}

		const displayText =
			topOption.option_value || topOption.option_value === 0
				? `${topOption.option_value} — ${topOption.option_text}`
				: topOption.option_text;

		return [displayText, (topOption.response_count / totalResponses) * 100];
	}, [optionsResults, totalResponses]);

	return (
		<div data-testid="top-response-cell" className="flex w-full items-center gap-1">
			{topResonseText ? (
				<Text size="paragraph-3" weight="regular" dangerouslySetClassName="py-line-clamp-1">
					{/* py-line-clamp-1 dynamically accounts for the available width of the parent element, adjusting for the width of the adjacent percentage chip! */}
					{topResonseText}
				</Text>
			) : null}
			{percentage && <Chip size={24} label={`${percentage.toFixed(0)}%`} color="primary" />}
		</div>
	);
};
